import { z } from 'zod';
import { assetUrl, IMG } from '@/lib/assets';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A centred client logo. Port of `Logo.vue`. Used by four deployed configs, all of them with
 * `{ logoFile, width: 12 }` and nothing else.
 *
 * The only widget that does **not** call `useWidgetLayout()`: legacy `Logo.vue` never called
 * `componentLayout()` either, so `options.layout` has no meaning here and no config sets one.
 * Its container is `.container.signage-logo` — `container` being the Tailwind utility that
 * `index.css` redefines with the legacy `center: true, padding: '2rem'` settings, so the
 * element is `margin-inline: auto; padding-inline: 2rem` plus the width below.
 */

const optionsSchema = z.object({
  /** Resolved from `img/logos/`. */
  logoFile: z.string().optional(),
  /** rem. Absent or `0` gives `100%`, matching the legacy truthiness check. */
  width: z.number().optional(),
});

export function Logo({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);

  if (!parsed.success) {
    warnOnce(
      `logo-options:${componentId}`,
      '[signage] Logo options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const { logoFile, width } = parsed.data;
  const logoUrl = logoFile ? assetUrl(`${IMG.logos}${logoFile}`) : undefined;

  return (
    <div className="container signage-logo" style={{ width: width ? `${String(width)}rem` : '100%' }}>
      <div className="flex justify-center">
        {/*
         * Legacy rendered the `<img>` unconditionally, so a missing or misspelled `logoFile`
         * threw inside webpack's `require()` and took the page with it. A miss warns once and
         * renders nothing here (CLAUDE.md constraint 4).
         */}
        {logoUrl !== undefined && <img src={logoUrl} className="m-auto w-60" alt="" />}
      </div>
    </div>
  );
}
